import '../../pages/TryoutAthletesPage.css';
import '../../pages/AddAthletePage.css';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AthleteAddedToast, {
  AthleteAddedToastPayload,
} from '../../components/common/AthleteAddedToast/AthleteAddedToast';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import PageTopNav from '../../components/common/PageTopNav/PageTopNav';
import type { AthleteStatus } from '../../types/athlete';
import DemoPhotoUpload from '../components/DemoPhotoUpload';
import { useDemoStore } from '../store/DemoStoreContext';

const TOAST_MS = 5000;

const DemoAddAthletePage: React.FC = () => {
  const navigate = useNavigate();
  const { tryoutMeta, addAthletePrefill, addOrReplaceUserAthlete } = useDemoStore();

  const positionChoices = tryoutMeta.athletePositions.length > 0
    ? tryoutMeta.athletePositions
    : ['Unknown'];
  const statusChoices = tryoutMeta.athleteStatuses;

  const [name, setName] = useState(addAthletePrefill.name);
  const [number, setNumber] = useState(addAthletePrefill.number);
  const [position, setPosition] = useState<string>(
    positionChoices.includes(addAthletePrefill.position)
      ? addAthletePrefill.position
      : positionChoices[0],
  );
  const [returningstatus, setReturningStatus] = useState<AthleteStatus>(
    addAthletePrefill.returningstatus ?? statusChoices[0] ?? null,
  );
  const [imageUrl, setImageUrl] = useState('');
  const [imageThumbnailUrl, setImageThumbnailUrl] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState<AthleteAddedToastPayload | null>(null);
  const [toastOpen, setToastOpen] = useState(true);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);

  const dismissToast = useCallback((): void => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setToastOpen(false);
  }, []);

  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedNumber = number.trim();
    if (!trimmedName) { setError('Athlete name is required.'); return; }
    if (!trimmedNumber) { setError('Athlete number is required.'); return; }
    setError('');

    addOrReplaceUserAthlete({
      name: trimmedName,
      number: trimmedNumber,
      position,
      returningstatus: statusChoices.length === 0 ? null : returningstatus,
      imageUrl: imageUrl.trim() || undefined,
      imageThumbnailUrl: imageThumbnailUrl.trim() || undefined,
    });

    const successPayload: AthleteAddedToastPayload = {
      name: trimmedName,
      number: trimmedNumber,
      position,
      returningstatus: statusChoices.length === 0 ? null : returningstatus,
      imageThumbnailUrl: imageThumbnailUrl.trim(),
    };

    contentScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });

    // Reset form fields like the real page does.
    setName('');
    setNumber('');
    setImageUrl('');
    setImageThumbnailUrl('');
    setPosition(positionChoices[0]);
    setReturningStatus(statusChoices[0] ?? null);

    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(successPayload);
    setToastOpen(true);
    toastTimerRef.current = setTimeout(() => setToastOpen(false), TOAST_MS);
  };

  return (
    <>
      <div className="tryout-athletes-shell add-athlete-shell">
        <PageTopNav
          onBack={() => navigate('/demo/tryout/athletes')}
          backAriaLabel="Back to athlete list"
          title={tryoutMeta.name}
        />

        <div ref={contentScrollRef} className="tryout-athletes-content">
          <div className="add-athlete-content-panel">
            <h2 className="add-athlete-heading">Add Athlete</h2>

            {error ? <div className="add-athlete-error" role="alert">{error}</div> : null}

            <form className="add-athlete-form" onSubmit={handleSubmit}>
              <Input
                type="text"
                label="Athlete Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoCapitalize="words"
              />
              <Input
                type="tel"
                label="Athlete Number"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                required
                inputMode="numeric"
              />

              <DemoPhotoUpload
                label="Athlete photo"
                className="add-athlete-photo-upload"
                imageUrl={imageUrl}
                onImageUpload={(payload) => {
                  setImageUrl(payload.imageUrl);
                  setImageThumbnailUrl(payload.imageThumbnailUrl);
                }}
              />

              <div className="add-athlete-field-group">
                <div className="add-athlete-field-label" id="demo-add-position-label">Position</div>
                <div
                  className="tryout-athletes-filter-row add-athlete-chip-row"
                  role="group"
                  aria-labelledby="demo-add-position-label"
                >
                  {positionChoices.map((pos) => (
                    <button
                      key={pos}
                      type="button"
                      className={[
                        'tryout-athletes-filter-chip',
                        position === pos ? 'tryout-athletes-filter-chip--active' : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => setPosition(pos)}
                      aria-pressed={position === pos}
                    >
                      {pos}
                    </button>
                  ))}
                </div>
              </div>

              {statusChoices.length > 0 ? (
                <div className="add-athlete-field-group">
                  <div className="add-athlete-field-label" id="demo-add-status-label">Status</div>
                  <div
                    className="tryout-athletes-filter-row add-athlete-chip-row"
                    role="group"
                    aria-labelledby="demo-add-status-label"
                  >
                    {statusChoices.map((label) => (
                      <button
                        key={label}
                        type="button"
                        className={[
                          'tryout-athletes-filter-chip',
                          returningstatus === label ? 'tryout-athletes-filter-chip--active' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={() => setReturningStatus(label)}
                        aria-pressed={returningstatus === label}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="add-athlete-actions">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate('/demo/tryout/athletes')}
                >
                  Cancel
                </Button>
                <Button type="submit">Add Athlete</Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {toast ? (
        <AthleteAddedToast
          open={toastOpen}
          payload={toast}
          onDismiss={dismissToast}
          onExitComplete={() => setToast(null)}
        />
      ) : null}
    </>
  );
};

export default DemoAddAthletePage;
